import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  ChevronRight, ChevronLeft, UserPlus, ClipboardList, CreditCard, 
  CheckCircle2, AlertCircle, Lightbulb, Play, Clock, Users,
  Smartphone, Mail, QrCode, Award, FileText, Printer,
  ArrowRight, Star, Zap, Shield, HelpCircle, Home,
  Monitor, MousePointer, Search, Eye, Download
} from 'lucide-react';
import { Link } from 'react-router-dom';

const WalkInRegistrationGuide = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [userRole, setUserRole] = useState('receptionist');

  const steps = [
    {
      id: 1,
      title: 'Navigate to Members Section',
      icon: Users,
      duration: '30 sec',
      content: {
        description: 'Access the member management area from your dashboard',
        instructions: [
          'Log in to your staff or admin dashboard',
          'Look for the "Members" tab in the sidebar navigation',
          'Click on "Members" to open the member management section',
          'You\'ll see a list of all existing members and an "Add Member" button'
        ],
        tips: [
          'Use the search bar to quickly find existing members before creating duplicates',
          'The member count displayed shows your current vs maximum allowed members'
        ],
        screenshot: '/help/screenshots/members-section.png'
      }
    },
    {
      id: 2,
      title: 'Click "Add Member" Button',
      icon: UserPlus,
      duration: '10 sec',
      content: {
        description: 'Initiate the new member registration process',
        instructions: [
          'Locate the "Add Member" button (usually top-right of the members list)',
          'Click the button to open the registration dialog',
          'A modal form will appear with fields for member information'
        ],
        tips: [
          'If the button is disabled, you may have reached your plan\'s member limit',
          'Contact your administrator to upgrade the plan if needed'
        ],
        screenshot: '/help/screenshots/add-member-button.png'
      }
    },
    {
      id: 3,
      title: 'Enter Patient Information',
      icon: ClipboardList,
      duration: '2-3 min',
      content: {
        description: 'Fill in the patient\'s personal and contact details',
        instructions: [
          'Enter the patient\'s full legal name (required)',
          'Input their email address (required - used for login)',
          'Add phone number for appointment reminders',
          'Record date of birth for age verification',
          'Input address for records and correspondence',
          'Add emergency contact name and phone number'
        ],
        fields: [
          { name: 'Full Name', required: true, example: 'John Michael Smith' },
          { name: 'Email', required: true, example: 'john.smith@email.com' },
          { name: 'Phone', required: false, example: '(555) 123-4567' },
          { name: 'Date of Birth', required: false, example: '1985-03-15' },
          { name: 'Address', required: false, example: '123 Main St, City, State 12345' },
          { name: 'Emergency Contact', required: false, example: 'Jane Smith' },
          { name: 'Emergency Phone', required: false, example: '(555) 987-6543' }
        ],
        tips: [
          'Double-check the email address - it will be used for login credentials',
          'Ask the patient to spell their name to avoid errors',
          'Use the address autocomplete feature for faster entry'
        ],
        screenshot: '/help/screenshots/patient-info-form.png'
      }
    },
    {
      id: 4,
      title: 'Select Membership Plan (Optional)',
      icon: Award,
      duration: '1 min',
      content: {
        description: 'Optionally assign a membership plan during registration',
        instructions: [
          'Review the available membership plans in the dropdown',
          'Select the plan that matches the patient\'s needs',
          'Or choose "No plan - Add later" to skip this step',
          'You can always assign or change plans after registration'
        ],
        tips: [
          'Discuss plan options with the patient before selecting',
          'Compare plan benefits and pricing to help them decide',
          'Some clinics prefer registering first, then discussing plans separately'
        ],
        screenshot: '/help/screenshots/select-plan.png'
      }
    },
    {
      id: 5,
      title: 'Process Payment (If Plan Selected)',
      icon: CreditCard,
      duration: '2-3 min',
      content: {
        description: 'Complete payment for the selected membership plan',
        instructions: [
          'Click "Continue to Payment Summary" to see the breakdown',
          'Review the plan price, enrollment fee (if applicable), and total',
          'Select a payment method from the dropdown',
          'For cash/check: Record the payment reference number',
          'For Stripe: Patient will complete card payment online',
          'Click "Confirm & Record Payment" to complete'
        ],
        paymentMethods: [
          { method: 'Cash', description: 'Accept physical cash payment', icon: '💵' },
          { method: 'Credit Card (Manual)', description: 'Process card through POS terminal', icon: '💳' },
          { method: 'Debit Card (Manual)', description: 'Process debit through POS terminal', icon: '💳' },
          { method: 'Bank Transfer', description: 'Accept wire or ACH transfer', icon: '🏦' },
          { method: 'Check', description: 'Accept personal or business check', icon: '📝' },
          { method: 'Stripe (Online)', description: 'Direct online card processing', icon: '⚡' }
        ],
        tips: [
          'Always provide a receipt for cash payments',
          'Record check numbers for reconciliation',
          'Stripe payments are processed in real-time and automatically recorded'
        ],
        screenshot: '/help/screenshots/payment-summary.png'
      }
    },
    {
      id: 6,
      title: 'Welcome Email & Credentials',
      icon: Mail,
      duration: '30 sec',
      content: {
        description: 'Send login credentials to the new member',
        instructions: [
          'Ensure "Send welcome email with login credentials" is checked',
          'Click "Create Member" to complete registration',
          'The system automatically generates a secure password',
          'An email is sent with login instructions and temporary password',
          'Inform the patient to check their email (including spam folder)'
        ],
        tips: [
          'Remind patients to change their password after first login',
          'If email doesn\'t arrive, you can resend from member details',
          'Provide the patient portal URL: www.mymedmember.com/patient'
        ],
        screenshot: '/help/screenshots/welcome-email.png'
      }
    },
    {
      id: 7,
      title: 'Registration Complete!',
      icon: CheckCircle2,
      duration: '-',
      content: {
        description: 'The patient is now registered in the system',
        instructions: [
          'The new member appears in your members list',
          'Their Patient ID is generated automatically (e.g., PAT-001234)',
          'They can now log in to the patient portal',
          'You can schedule appointments, record notes, and more'
        ],
        nextSteps: [
          'Schedule their first appointment',
          'Print a member card if needed',
          'Add any medical notes or history',
          'Set up appointment reminders'
        ],
        screenshot: '/help/screenshots/registration-complete.png'
      }
    }
  ];

  const quickTips = [
    {
      icon: Zap,
      title: 'Fast Check-in Mode',
      description: 'For returning patients, use the QR scanner or search to quickly check them in without re-registration.'
    },
    {
      icon: Shield,
      title: 'Privacy Reminder',
      description: 'Always ensure patient information is entered on a private screen away from other patients.'
    },
    {
      icon: Clock,
      title: 'Peak Hours',
      description: 'During busy times, register patients first with minimal info, then complete details when time permits.'
    },
    {
      icon: Smartphone,
      title: 'Self-Registration',
      description: 'Patients can also self-register via the patient portal or embedded website widget.'
    }
  ];

  const faqs = [
    {
      question: 'What if the patient doesn\'t have an email address?',
      answer: 'You can create a placeholder email using their phone number (e.g., 5551234567@clinic.placeholder.com). However, they won\'t receive automated notifications. Consider helping them set up a free email account for better service.'
    },
    {
      question: 'Can I register a patient without assigning a plan?',
      answer: 'Yes! Select "No plan - Add later" during registration. You can assign or change plans anytime from the member\'s profile or by clicking the "Assign Plan" button in the members list.'
    },
    {
      question: 'What if the patient wants to pay later?',
      answer: 'Register them without selecting a plan first. When they\'re ready to pay, open their profile, click "Assign Plan", and complete the payment process then.'
    },
    {
      question: 'How do I handle family members?',
      answer: 'Register each family member individually. Family linking features are available in the member profile settings. Some plans offer family discounts - check your plan configurations.'
    },
    {
      question: 'What if the email is already registered?',
      answer: 'The system will show an error if the email already exists. Search for the existing member instead. If they forgot their password, use the "Reset Password" function from their profile.'
    },
    {
      question: 'Can I edit information after registration?',
      answer: 'Absolutely! Click on any member in the list to view their profile. Use the "Edit" button to update their information, contact details, or membership plan.'
    }
  ];

  const troubleshooting = [
    {
      issue: '"Add Member" button is disabled',
      solutions: [
        'You may have reached your plan\'s member limit',
        'Check your subscription status in Settings > Subscription',
        'Contact your administrator to upgrade the plan'
      ]
    },
    {
      issue: 'Patient didn\'t receive welcome email',
      solutions: [
        'Ask them to check spam/junk folder',
        'Verify the email address is correct',
        'Go to member profile and click "Resend Welcome Email"',
        'Check if your clinic\'s email settings are configured correctly'
      ]
    },
    {
      issue: 'Payment failed',
      solutions: [
        'For Stripe: Ask patient to try a different card',
        'Check if your Stripe account is properly connected',
        'Use an alternative payment method (cash, check)',
        'Contact support if the issue persists'
      ]
    },
    {
      issue: 'Form validation errors',
      solutions: [
        'Ensure all required fields (name, email) are filled',
        'Check email format is valid (contains @ and domain)',
        'Phone numbers should be in standard format',
        'Clear any special characters from the name field'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/help" className="flex items-center gap-2 text-teal-600 hover:text-teal-700 transition-colors">
                <ChevronLeft className="h-5 w-5" />
                <span className="font-medium">Help Center</span>
              </Link>
              <span className="text-gray-300">|</span>
              <span className="text-gray-600 font-medium">Walk-In Registration Guide</span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" asChild>
                <Link to="/admin">
                  <Home className="h-4 w-4 mr-2" />
                  Dashboard
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <UserPlus className="h-4 w-4" />
            Step-by-Step Guide
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Walk-In Patient Registration
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            A complete guide for clinic staff to register walk-in patients quickly and efficiently
          </p>
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="h-5 w-5 text-teal-600" />
              <span>~5 minutes total</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <ClipboardList className="h-5 w-5 text-teal-600" />
              <span>{steps.length} steps</span>
            </div>
            <Badge className="bg-green-100 text-green-700 border-0">
              Beginner Friendly
            </Badge>
          </div>
        </div>

        {/* Role Selector */}
        <Card className="mb-8 border-2 border-teal-100 bg-gradient-to-r from-teal-50/50 to-cyan-50/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-teal-600" />
                <span className="font-medium text-gray-700">I am a:</span>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant={userRole === 'receptionist' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUserRole('receptionist')}
                  className={userRole === 'receptionist' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                >
                  Receptionist
                </Button>
                <Button 
                  variant={userRole === 'admin' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUserRole('admin')}
                  className={userRole === 'admin' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                >
                  Clinic Admin
                </Button>
                <Button 
                  variant={userRole === 'staff' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setUserRole('staff')}
                  className={userRole === 'staff' ? 'bg-teal-600 hover:bg-teal-700' : ''}
                >
                  Staff Member
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <Tabs defaultValue="step-by-step" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 h-auto p-1 bg-gray-100">
            <TabsTrigger value="step-by-step" className="py-3 data-[state=active]:bg-white">
              <Play className="h-4 w-4 mr-2" />
              Step-by-Step
            </TabsTrigger>
            <TabsTrigger value="quick-reference" className="py-3 data-[state=active]:bg-white">
              <Zap className="h-4 w-4 mr-2" />
              Quick Reference
            </TabsTrigger>
            <TabsTrigger value="faq" className="py-3 data-[state=active]:bg-white">
              <HelpCircle className="h-4 w-4 mr-2" />
              FAQ
            </TabsTrigger>
            <TabsTrigger value="troubleshooting" className="py-3 data-[state=active]:bg-white">
              <AlertCircle className="h-4 w-4 mr-2" />
              Troubleshooting
            </TabsTrigger>
          </TabsList>

          {/* Step-by-Step Tab */}
          <TabsContent value="step-by-step" className="space-y-6">
            {/* Progress Indicator */}
            <div className="flex items-center justify-between px-4">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => setCurrentStep(index)}
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                      index < currentStep 
                        ? 'bg-teal-600 border-teal-600 text-white' 
                        : index === currentStep 
                          ? 'bg-white border-teal-600 text-teal-600 shadow-lg scale-110' 
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                    }`}
                  >
                    {index < currentStep ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <span className="font-semibold">{step.id}</span>
                    )}
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`w-12 h-1 mx-1 rounded ${
                      index < currentStep ? 'bg-teal-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>

            {/* Current Step Card */}
            <Card className="border-2 border-teal-200 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-teal-600 flex items-center justify-center shadow-lg">
                      {React.createElement(steps[currentStep].icon, { className: "h-7 w-7 text-white" })}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-teal-700 border-teal-300">
                          Step {steps[currentStep].id} of {steps.length}
                        </Badge>
                        <Badge variant="outline" className="text-gray-600">
                          <Clock className="h-3 w-3 mr-1" />
                          {steps[currentStep].duration}
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl text-gray-900">
                        {steps[currentStep].title}
                      </CardTitle>
                    </div>
                  </div>
                </div>
                <CardDescription className="text-base mt-2 text-gray-600">
                  {steps[currentStep].content.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="py-6 space-y-6">
                {/* Instructions */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-teal-600" />
                    Instructions
                  </h4>
                  <ol className="space-y-3">
                    {steps[currentStep].content.instructions.map((instruction, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-semibold">
                          {idx + 1}
                        </span>
                        <span className="text-gray-700 pt-0.5">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Fields Table (for Step 3) */}
                {steps[currentStep].content.fields && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-teal-600" />
                      Form Fields
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Field</th>
                            <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Required</th>
                            <th className="text-left px-4 py-2 text-sm font-semibold text-gray-700">Example</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {steps[currentStep].content.fields.map((field, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium text-gray-900">{field.name}</td>
                              <td className="px-4 py-3">
                                {field.required ? (
                                  <Badge className="bg-red-100 text-red-700 border-0">Required</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-gray-500">Optional</Badge>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600 font-mono text-sm">{field.example}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Payment Methods (for Step 5) */}
                {steps[currentStep].content.paymentMethods && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-teal-600" />
                      Available Payment Methods
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {steps[currentStep].content.paymentMethods.map((pm, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <span className="text-2xl">{pm.icon}</span>
                          <div>
                            <p className="font-medium text-gray-900">{pm.method}</p>
                            <p className="text-sm text-gray-500">{pm.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Steps (for last step) */}
                {steps[currentStep].content.nextSteps && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <ArrowRight className="h-5 w-5 text-teal-600" />
                      What's Next?
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {steps[currentStep].content.nextSteps.map((next, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{next}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tips */}
                {steps[currentStep].content.tips && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <Lightbulb className="h-5 w-5" />
                      Pro Tips
                    </h4>
                    <ul className="space-y-2">
                      {steps[currentStep].content.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-amber-900">
                          <Star className="h-4 w-4 mt-1 flex-shrink-0 text-amber-600" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>

              {/* Navigation Buttons */}
              <div className="px-6 pb-6 flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                  disabled={currentStep === 0}
                  className="gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous Step
                </Button>
                
                <div className="text-sm text-gray-500">
                  Step {currentStep + 1} of {steps.length}
                </div>
                
                <Button
                  onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
                  disabled={currentStep === steps.length - 1}
                  className="gap-2 bg-teal-600 hover:bg-teal-700"
                >
                  Next Step
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          </TabsContent>

          {/* Quick Reference Tab */}
          <TabsContent value="quick-reference" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-teal-600" />
                  Quick Reference Checklist
                </CardTitle>
                <CardDescription>
                  Print this checklist and keep it at the front desk
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <div key={step.id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">
                        {step.id}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{step.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{step.content.description}</p>
                      </div>
                      <Badge variant="outline" className="flex-shrink-0">
                        <Clock className="h-3 w-3 mr-1" />
                        {step.duration}
                      </Badge>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 flex justify-center">
                  <Button variant="outline" className="gap-2">
                    <Printer className="h-4 w-4" />
                    Print Checklist
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Tips Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {quickTips.map((tip, index) => (
                <Card key={index} className="border-l-4 border-l-teal-500">
                  <CardContent className="py-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <tip.icon className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{tip.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{tip.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-teal-600" />
                  Frequently Asked Questions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left hover:text-teal-600">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Troubleshooting Tab */}
          <TabsContent value="troubleshooting">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  Common Issues & Solutions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {troubleshooting.map((item, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                      <div className="bg-red-50 px-4 py-3 border-b border-red-100">
                        <h4 className="font-semibold text-red-800 flex items-center gap-2">
                          <AlertCircle className="h-4 w-4" />
                          {item.issue}
                        </h4>
                      </div>
                      <div className="p-4 bg-white">
                        <h5 className="text-sm font-semibold text-gray-700 mb-2">Solutions:</h5>
                        <ul className="space-y-2">
                          {item.solutions.map((solution, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-600">
                              <CheckCircle2 className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
                              <span>{solution}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Still Need Help */}
        <Card className="mt-8 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <HelpCircle className="h-6 w-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">Still need assistance?</h3>
                  <p className="text-gray-600">Our support team is available to help you with any questions</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" asChild>
                  <Link to="/help">
                    Browse Help Center
                  </Link>
                </Button>
                <Button 
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => window.location.href = 'mailto:support@mymedmember.com?subject=Walk-In%20Registration%20Support%20Request'}
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WalkInRegistrationGuide;
